import type { AuthService } from './services/auth';
import { createLaravelAuthService } from './services/auth';
import type { AccountService } from './services/account';
import { createLaravelAccountService } from './services/account';
import type { CartService } from './services/cart';
import {
  createLaravelCollectionService,
  type CollectionService,
} from './services/collection/CollectionService';
import { createLaravelCartService } from './services/cart';
import { createLaravelPromotionService, type PromotionService } from './services/promotion';
import type { CategoryService } from './services/category';
import { createLaravelCategoryService } from './services/category';
import type { OrderService } from './services/order';
import { createLaravelOrderService } from './services/order';
import type { ProductService } from './services/product';
import { createLaravelProductService } from './services/product';
import type { SliderService } from './services/slider';
import { createLaravelSliderService } from './services/slider';
import type { PartnerService } from './services/partner';
import { createLaravelPartnerService } from './services/partner';
import type { PageSectionService } from './services/page-section';
import { createLaravelPageSectionService } from './services/page-section';
import type { NewsService } from './services/news';
import { createLaravelNewsService } from './services/news';
import type { ContactService } from './services/contact';
import { createLaravelContactService } from './services/contact';
import type { WishlistService } from './services/wishlist';
import { createLaravelWishlistService } from './services/wishlist';
import type { TranslationService } from './services/translation/createLaravelTranslationService';
import { createLaravelTranslationService } from './services/translation/createLaravelTranslationService';
import type { Transport } from './transport/Transport';
import { createLaravelActivityService, type ActivityService } from './services/activity/ActivityService';
import { createLaravelMetaService, type MetaService } from './services/meta/MetaService';
import { createGeocodingService, type GeocodingService } from './http/geocoding';
import {
  createLaravelTransport,
  type LaravelTransportOptions,
} from './transport/createLaravelTransport';

/**
 * Single object the app holds onto. Features access services through this registry:
 *
 *   const api = createApiClient({ backend: 'laravel', baseUrl, … });
 *   const session = await api.auth.login({ email, password });
 *   const page = await api.products.list({ page: 1 });
 *
 * Adding a new resource = adding a property here. Adding a new backend = adding a
 * branch to the factory below; nothing else in the app changes.
 */
export interface ApiClient {
  readonly auth: AuthService;
  readonly account: AccountService;
  readonly products: ProductService;
  readonly categories: CategoryService;
  readonly cart: CartService;
  readonly promotions: PromotionService;
  readonly collections: CollectionService;
  readonly wishlist: WishlistService;
  readonly orders: OrderService;
  readonly sliders: SliderService;
  readonly partners: PartnerService;
  readonly pageSections: PageSectionService;
  readonly news: NewsService;
  readonly contact: ContactService;
  readonly translations: TranslationService;
  readonly geocoding: GeocodingService;
  readonly activity: ActivityService;
  readonly meta: MetaService;
}

export type ApiClientOptions =
  | ({ readonly backend: 'laravel' } & LaravelTransportOptions)
  | { readonly backend: 'custom'; readonly transport: Transport };

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const transport = resolveTransport(opts);
  return {
    auth: createLaravelAuthService(transport),
    account: createLaravelAccountService(transport),
    products: createLaravelProductService(transport),
    categories: createLaravelCategoryService(transport),
    cart: createLaravelCartService(transport),
    promotions: createLaravelPromotionService(transport),
    collections: createLaravelCollectionService(transport),
    wishlist: createLaravelWishlistService(transport),
    orders: createLaravelOrderService(transport),
    sliders: createLaravelSliderService(transport),
    partners: createLaravelPartnerService(transport),
    pageSections: createLaravelPageSectionService(transport),
    news: createLaravelNewsService(transport),
    contact: createLaravelContactService(transport),
    translations: createLaravelTranslationService(transport),
    geocoding: createGeocodingService(),
    activity: createLaravelActivityService(transport),
    meta: createLaravelMetaService(transport),
  };
}

function resolveTransport(opts: ApiClientOptions): Transport {
  if (opts.backend === 'laravel') return createLaravelTransport(opts);
  return opts.transport;
}
